import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDraggableSheet } from "../../../helpers/hooks";
import "./ContributionSection.scss";
import ProfilePicture from "../ProfilePicture/ProfilePicture";
import { useLocation } from "react-router-dom";

import { loadStripe } from '@stripe/stripe-js';
import { createPaymentSession } from "../../Reducers/actions/paymentActions";
import Icon from "../Icon/Icon";
import StripeTrustButtonInner from "../StripeTrustButtonInner/StripeTrustButtonInner";
import { useSoftwall } from "../../containers/SoftWall/SoftWall";
import { HYK_FEE_PERCENT, STRIPE_FEE_PERCENT, STRIPE_FIXED_FEE, VAT_PERCENT } from "../../constants/globals";
import { savePendingContributionIntent } from "../../constants/signupFlow";
import { getProfilePublicPath } from "../../../helpers/profilePaths";

const FEE_SWIPE_THRESHOLD_PX = 48;

// Check if pathname contains "profile" (which means bottom nav is visible)
const isBottomNavVisible = (pathname) => {
    return pathname.includes('profile');
};

const contributionTypes = [
    { label: "MONTHLY", value: "MONTHLY", icon: "monthly-contribution" },
    { label: "ONE-TIME", value: "ONE_TIME", icon: "one-time-contribution" },
]

function ContributionSection({ onClose, creation, allowBackgroundTouch = false, twoSnapFeeSheet = false }) {
    const getStripeSessionData = (response) => {
        const sessionId =
            response?.id ||
            response?.sessionId ||
            response?.session_id ||
            response?.data?.id ||
            response?.data?.sessionId ||
            response?.data?.session_id;
        const sessionUrl =
            response?.url ||
            response?.checkout_url ||
            response?.data?.url ||
            response?.data?.checkout_url;
        return { sessionId, sessionUrl };
    };
    const [inputFocused, setInputFocused] = useState(false);
    const [amount, setAmount] = useState(null);
    const [type, setType] = useState("MONTHLY");
    const [loading, setLoading] = useState(false);
    const [showFeeBreakdown, setShowFeeBreakdown] = useState(false);
    const [feeDetailsExpanded, setFeeDetailsExpanded] = useState(false);
    const feeSwipeStartYRef = useRef(null);
    const sw = useSoftwall();
    const location = useLocation();
    
    // Check if bottom nav bar is visible (using same logic as Layout.jsx)
    const bottomNavVisible = isBottomNavVisible(location.pathname);

    // Fee calculations
    const calculateFees = (baseAmount) => {
        if (!baseAmount) return { hykFee: 0, stripeFee: 0, vatAmount: 0, totalToPay: 0, creatorGets: baseAmount || 0 };

        const hykFee = baseAmount * HYK_FEE_PERCENT;
        const stripeFee = (baseAmount * STRIPE_FEE_PERCENT) + STRIPE_FIXED_FEE;
        const subtotal = baseAmount + hykFee + stripeFee;
        const vatAmount = subtotal * VAT_PERCENT;
        const totalToPay = subtotal + vatAmount;

        return {
            hykFee: Math.round(hykFee * 100) / 100,
            stripeFee: Math.round(stripeFee * 100) / 100,
            vatAmount: Math.round(vatAmount * 100) / 100,
            totalToPay: Math.round(totalToPay * 100) / 100,
            creatorGets: baseAmount
        };
    };

    const fees = calculateFees(amount);
    const hasContributionAmount = Boolean(amount && amount > 0);

    const displayFullFeeBreakdown = twoSnapFeeSheet
        ? feeDetailsExpanded && hasContributionAmount
        : showFeeBreakdown;

    // Show fee breakdown when amount is selected (standard sheet); contribute route uses two-snap instead
    useEffect(() => {
        if (twoSnapFeeSheet) {
            setShowFeeBreakdown(false);
            return;
        }
        setShowFeeBreakdown(Boolean(amount && amount > 0));
    }, [amount, twoSnapFeeSheet]);

    useEffect(() => {
        if (twoSnapFeeSheet) {
            setFeeDetailsExpanded(false);
        }
    }, [amount, twoSnapFeeSheet]);

    // Keypad handlers
    const handleKeypadInput = (digit) => {
        const currentValue = amount ? amount.toString() : '';
        const newValue = currentValue + digit;
        const numValue = parseInt(newValue);
        if (numValue <= 9999) { // Limit to reasonable amount
            setAmount(numValue);
        }
    };

    const handleKeypadDelete = () => {
        if (amount) {
            const currentValue = amount.toString();
            if (currentValue.length === 1) {
                setAmount(null);
            } else {
                const newValue = currentValue.slice(0, -1);
                setAmount(parseInt(newValue));
            }
        }
    };

    const {
        sheetStyle,
        overlayHandlers,
    } = useDraggableSheet({ onClose });

    const endFeeSheetSwipe = (clientY) => {
        const start = feeSwipeStartYRef.current;
        feeSwipeStartYRef.current = null;
        if (start === null || !twoSnapFeeSheet || !hasContributionAmount) return;
        const dy = clientY - start;
        if (Math.abs(dy) < FEE_SWIPE_THRESHOLD_PX) return;
        if (dy > 0 && feeDetailsExpanded) {
            setFeeDetailsExpanded(false);
        } else if (dy < 0 && !feeDetailsExpanded) {
            setFeeDetailsExpanded(true);
        }
    };

    const handleFeePullMouseDown = (e) => {
        if (!twoSnapFeeSheet || !hasContributionAmount) return;
        e.preventDefault();
        feeSwipeStartYRef.current = e.clientY;
        const handleMouseUp = (ev) => {
            endFeeSheetSwipe(ev.clientY);
            window.removeEventListener('mouseup', handleMouseUp);
        };
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleFeePullKeyDown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (!twoSnapFeeSheet || !hasContributionAmount) return;
        e.preventDefault();
        setFeeDetailsExpanded((prev) => !prev);
    };

    const addToUrlQueryFilter = (key, value) => {
        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set(key, value);
        } else {
            url.searchParams.delete(key);
        }
        return url.toString();
    };

    const getCreatorProfileSuccessUrl = () => {
        const creatorIdentifier =
            creation?.creator_id ||
            creation?.users?.user_id ||
            creation?.users?.id;

        if (!creatorIdentifier) {
            return addToUrlQueryFilter("success", "true");
        }

        const profileUser = creation?.users;
        const path =
            getProfilePublicPath(profileUser) || `/profiles/${creatorIdentifier}`;
        const url = new URL(path, window.location.origin);
        url.searchParams.set("success", "true");
        return url.toString();
    };

    const handleSend = async () => {

        if (!sw.requireAuth("Contribute to Creation", `Sign up or log in to be able to contribute to ${creation?.users?.userName || ''}`)) {
            savePendingContributionIntent({
                source: 'contribute',
                type,
                amount,
                creation: {
                    id: creation?.creation_id,
                    title: creation?.title,
                    description: creation?.description,
                    creator_id: creation?.creator_id,
                    imageUrl: creation?.media?.[0]?.media_url || creation?.thumbnail_url || creation?.media_url,
                    users: creation?.users
                        ? {
                            userName: creation.users.userName,
                            username: creation.users.username,
                            user_id: creation.users.user_id || creation.users.id,
                        }
                        : undefined,
                },
                cancel_url: window.location.href,
            });
            onClose?.();
            return;
        }
        setLoading(true);
        let isRedirecting = false;
        try {
            const stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
            const data = {
                type: type === "MONTHLY" ? "subscription" : "payment",
                amount: amount, // Send base amount only - backend will calculate total with all fees
                currency: "eur",
                success_url: getCreatorProfileSuccessUrl(),
                cancel_url: `${window.location.href}`,
                creation: {
                    id: creation?.creation_id,
                    title: creation?.title,
                    description: creation?.description,
                    creator_id: creation?.creator_id,
                    imageUrl: creation?.media?.[0]?.media_url || creation?.thumbnail_url || creation?.media_url,
                }
            };
            const sessionResponse = await createPaymentSession(data);
            const { sessionId, sessionUrl } = getStripeSessionData(sessionResponse);
            if (sessionUrl) {
                isRedirecting = true;
                window.location.href = sessionUrl;
                return;
            }
            if (!sessionId) {
                throw new Error('Missing Stripe session id');
            }

            const result = await stripe.redirectToCheckout({
                sessionId,
            });

            if (result.error) {
                console.error(result.error.message);
            }
        } catch (error) {
            console.error('Payment session error:', error);
            if (error.response) {
                console.error('Server response:', error.response);
            }
        } finally {
            if (!isRedirecting) {
                setLoading(false);
            }
        }
    };

    const modalContent = (
        <div
            className={`contribution-section ${allowBackgroundTouch ? 'allow-background-touch' : ''}`}
            {...overlayHandlers}
        >
            <div
                className={`sheet ${inputFocused ? "expanded" : ""} ${twoSnapFeeSheet ? "sheet--two-snap-fees" : ""}`}
                style={sheetStyle}
                onClick={(e) => e.stopPropagation()}
            >
                {twoSnapFeeSheet && hasContributionAmount && (
                    <div
                        className="contribution-sheet-pull-handle"
                        role="button"
                        tabIndex={0}
                        aria-expanded={feeDetailsExpanded}
                        aria-controls="contribution-fee-details"
                        aria-label={
                            feeDetailsExpanded
                                ? 'Drag down or press Enter to collapse fee details'
                                : 'Drag up or press Enter to expand fee details'
                        }
                        onKeyDown={handleFeePullKeyDown}
                        onTouchStart={(e) => {
                            feeSwipeStartYRef.current = e.touches[0].clientY;
                        }}
                        onTouchEnd={(e) => {
                            const touch = e.changedTouches?.[0];
                            if (!touch) return;
                            endFeeSheetSwipe(touch.clientY);
                        }}
                        onMouseDown={handleFeePullMouseDown}
                    >
                        <div className="pull-bar" aria-hidden />
                    </div>
                )}
                {inputFocused && <p className="title mb-12 mt-16 fs-regular fg-dark fw-bold">Contribute to {creation?.users?.userName || ''}</p>}

                {!inputFocused && <div className="creator-box d-flex f-column aic jcc p-24">
                    {!allowBackgroundTouch && <ProfilePicture
                        imgUrl={creation?.users?.profilePicture}
                        width={56}
                        height={56}
                        no_edit={true}
                    />}
                    <h3 className="title mb-12 mt-16">Contribute to <span>{creation?.users?.userName || ''}</span></h3>
                    {!allowBackgroundTouch && <div className="caption mb-8">
                        Your contribution helps the artist keep sharing magic — one creation
                        at a time.
                    </div>}
                </div>}

                <div className="contribution-box d-flex px-16 mb-24 gap-8">
                    {contributionTypes.map((item, index) => (
                        <div
                            key={index}
                            className={`type-box ${type === item.value ? "active" : ""}`}
                            onClick={() => setType(item.value)}
                        >
                            <div className="type-icon">
                                <Icon alias={item.icon} color={type === item.value ? "#FFF" : "#FF3232"} width={48} height={48} />
                            </div>
                            <div className={`type-label ${type === item.value ? "active" : ""}`}>{item.label}</div>
                        </div>
                    ))}
                </div>
                <div className="amount-buttons d-flex px-16 mb-16 gap-8">
                    {[5, 10, 25, 50].map((value) => (
                        <button
                            key={value}
                            className={`amount-btn btn btn-full btn-sm ${amount === value ? 'active' : ''}`}
                            onClick={() => setAmount(value)}
                        >
                            <span>€{value}</span>
                        </button>
                    ))}
                </div>

                <div className="input-box d-flex px-16 mb-16">
                    <div className="input-wrapper">
                        <input
                            type="text"
                            inputMode="decimal"
                            id="amount"
                            name="amount"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            className="input-field-secondary"
                            value={amount || ''}
                            onChange={(e) => setAmount(parseInt(e.target.value))}
                            placeholder="Other amount"
                            required
                            onFocus={() => {
                                setInputFocused(true);

                            }}
                            onBlur={() => {
                                setInputFocused(false);
                            }}
                        />
                        <div className="symbol">€</div>
                    </div>
                </div>

                {!amount && (
                    <div className="validation-message px-16 mb-16">
                        <span className="error-text">Set or select an amount to contribute</span>
                    </div>
                )}

                {twoSnapFeeSheet && hasContributionAmount && !feeDetailsExpanded && (
                    <div className="fee-breakdown__collapsed-preview px-16 mb-16">
                        <div className="fee-breakdown__collapsed-total d-flex jcsb aic mb-12">
                            <span className="fee-label">Total to pay</span>
                            <span className="fee-amount">€ {fees.totalToPay}</span>
                        </div>
                        <div className="fee-breakdown__expand-row d-flex jcc">
                            <button
                                type="button"
                                className="fee-breakdown__sheet-link mb-48  "
                                onClick={() => setFeeDetailsExpanded(true)}
                                aria-expanded="false"
                                aria-controls="contribution-fee-details"
                                id="contribution-fee-summary"
                            >
                                Show more
                            </button>
                        </div>
                    </div>
                )}

                {displayFullFeeBreakdown && (
                    <div
                        id="contribution-fee-details"
                        role="region"
                        aria-label="Fee breakdown"
                        className="fee-breakdown px-16 mb-48"
                    >
                        {twoSnapFeeSheet && (
                            <div className="fee-breakdown__collapse-row d-flex jcfe mb-12">
                                <button
                                    type="button"
                                    className="fee-breakdown__sheet-link"
                                    onClick={() => setFeeDetailsExpanded(false)}
                                >
                                    Show less
                                </button>
                            </div>
                        )}
                        <div className="fee-header mb-12">
                            <span className="fee-message fw-bold">These fees keep HYK fair, independent and free of ads</span>
                        </div>
                        <div className="fee-details">
                            <div className="fee-row d-flex jcsb aic mb-8 ">
                                <span className="fee-label">Total to pay</span>
                                <span className="fee-amount">€ {fees.totalToPay}</span>
                            </div>
                            <div className="fee-row d-flex jcsb aic mb-8">
                                <span className="fee-label">Creator gets exactly</span>
                                <span className="fee-amount">€ {fees.creatorGets}</span>
                            </div>
                            <div className="fee-row d-flex jcsb aic mb-8">
                                <span className="fee-label">HYK fees (10%)</span>
                                <span className="fee-amount">€ {fees.hykFee}</span>
                            </div>
                            <div className="fee-row d-flex jcsb aic">
                                <span className="fee-label">Stripe fees (1.5% + € 0.25)</span>
                                <span className="fee-amount">€ {fees.stripeFee}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="submit-box d-flex f-column mt-16">
                    <button
                        onClick={handleSend}
                        type="button"
                        disabled={(amount || 0) <= 0}
                        className="button-contribute-red"
                    >
                        <StripeTrustButtonInner centerTitle="Contribute" variant="solid" />
                    </button>
                </div>


            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}

export default ContributionSection;
